import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Pagination from '../../components/ui/Pagination';
import RequestStatusBadge from '../../components/request-builder/RequestStatusBadge';
import { formatRequestAmount } from '../../components/request-builder/requestBuilderUtils';
import repositoryContainer from '../../services/RepositoryContainer';
import WorkflowTaskDetailsModal from './components/WorkflowTaskDetailsModal';
import { useNotifications } from '../../components/context/NotificationContext';

const PAGE_SIZE = 20;

const getApiErrorMessage = (error, fallback) => (
  error?.data?.message || (typeof error?.data === 'string' ? error.data : null) || fallback
);

const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GH', options).format(date);
};

const getItemSummary = (request) => {
  const items = request?.items || [];
  if (items.length === 0) return 'No items';
  if (items.length === 1) return items[0].name;
  return `${items.length} items`;
};

const PaymentBadge = ({ paid }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-caption font-medium ${
    paid ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
  }`}>
    {paid ? 'Paid' : 'Pending'}
  </span>
);

const MyTasks = () => {
  const { workflowRefreshVersion, refreshAssignedTaskCount } = useNotifications();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTasks = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const raw = await repositoryContainer.workflowInstance.listAssignedToMe();
      const response = raw?.response ?? raw;
      setTasks(Array.isArray(response) ? response : []);
    } catch (loadError) {
      if (!silent) {
        setError(getApiErrorMessage(loadError, 'Assigned tasks could not be loaded.'));
        setTasks([]);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (workflowRefreshVersion > 0) loadTasks({ silent: true });
  }, [loadTasks, workflowRefreshVersion]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const filteredTasks = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tasks
      .filter((task) => {
        if (!query) return true;
        const request = task.bookingRequest || {};
        return [
          request.referenceCode,
          request.clientName,
          request.clientEmail,
          request.clientPhone,
          task.workflowName,
          task.currentStage?.name,
          ...(request.items || []).map((item) => item.name),
        ].some((value) => value?.toString().toLowerCase().includes(query));
      })
      .sort((first, second) => new Date(second.updatedAt || second.createdAt || 0) - new Date(first.updatedAt || first.createdAt || 0));
  }, [searchQuery, tasks]);

  const totalPages = Math.ceil(filteredTasks.length / PAGE_SIZE);
  const visibleTasks = filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const paidCount = tasks.filter((task) => task.bookingRequest?.paymentCompletedAt).length;

  const handleTasksChanged = useCallback(async () => {
    await Promise.all([
      loadTasks({ silent: true }),
      refreshAssignedTaskCount(),
    ]);
  }, [loadTasks, refreshAssignedTaskCount]);

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
      />

      <div className={`transition-all duration-250 ease-elegant ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation isSidebarCollapsed={isSidebarCollapsed} />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="mb-2 font-heading text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">My Tasks</h1>
                <p className="text-sm text-muted-foreground font-caption md:text-base">
                  Review booking requests currently assigned to you.
                </p>
              </div>
              <Button
                variant="outline"
                iconName="RefreshCw"
                loading={isLoading}
                onClick={() => { loadTasks(); refreshAssignedTaskCount(); }}
              >
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground font-caption">Awaiting your decision</p>
                <p className="mt-1 font-heading text-xl sm:text-2xl font-bold text-foreground">{tasks.length}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground font-caption">Payment confirmed</p>
                <p className="mt-1 font-heading text-xl sm:text-2xl font-bold text-foreground">{paidCount}</p>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 md:p-5">
              <Input
                type="search"
                label="Search tasks"
                placeholder="Reference, customer, stage, workflow, or item..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>

            {isLoading ? (
              <div className="space-y-4" aria-label="Loading assigned tasks">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-24 animate-pulse rounded-xl border border-border bg-card" />
                ))}
              </div>
            ) : error ? (
              <div className="rounded-xl border border-error/20 bg-error/5 p-8 text-center">
                <Icon name="AlertCircle" size={34} color="var(--color-error)" className="mx-auto mb-3" />
                <p className="text-sm text-error font-caption">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  iconName="RefreshCw"
                  onClick={() => { loadTasks(); refreshAssignedTaskCount(); }}
                >
                  Try Again
                </Button>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-card px-5 py-14 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Icon name="ListChecks" size={28} />
                </div>
                <h2 className="mt-4 font-heading text-xl font-semibold text-foreground">
                  {tasks.length === 0 ? 'No assigned tasks' : 'No matching tasks'}
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground font-caption">
                  {tasks.length === 0
                    ? 'Requests will appear here when their current workflow stage is assigned to you.'
                    : 'Try changing your search terms.'}
                </p>
                {searchQuery && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setSearchQuery('')}>Clear Search</Button>
                )}
              </div>
            ) : (
              <>
                <div className="hidden overflow-hidden rounded-xl border border-border bg-card lg:block">
                  <div className="overflow-auto custom-scrollbar">
                    <table className="w-full">
                      <thead className="border-b border-border bg-muted">
                        <tr>
                          {['Reference', 'Customer', 'Current Stage', 'Schedule', 'Total', 'Payment', 'Status', 'Actions'].map((heading) => (
                            <th key={heading} className="whitespace-nowrap px-4 py-4 text-left text-sm font-caption font-semibold text-foreground">
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleTasks.map((task) => {
                          const request = task.bookingRequest || {};
                          return (
                            <tr key={task.id} className="border-b border-border transition-colors last:border-b-0 hover:bg-muted/40">
                              <td className="px-4 py-4 font-mono text-xs font-semibold text-primary">{request.referenceCode}</td>
                              <td className="px-4 py-4">
                                <p className="text-sm text-foreground font-caption font-medium">{request.clientName}</p>
                                <p className="mt-1 text-xs text-muted-foreground font-caption">{getItemSummary(request)}</p>
                              </td>
                              <td className="px-4 py-4">
                                <p className="text-sm text-foreground font-caption font-medium">{task.currentStage?.name}</p>
                                <p className="mt-1 text-xs text-muted-foreground font-caption">{task.workflowName}</p>
                              </td>
                              <td className="px-4 py-4 text-sm text-foreground font-caption">
                                {request.requestedDate ? (
                                  <>
                                    <p>{formatDate(request.requestedDate)}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">{request.requestedTime}</p>
                                  </>
                                ) : <span className="text-muted-foreground">Not required</span>}
                              </td>
                              <td className="whitespace-nowrap px-4 py-4 text-sm text-foreground font-caption font-semibold">
                                {formatRequestAmount(request.totalAmount)}
                              </td>
                              <td className="px-4 py-4"><PaymentBadge paid={Boolean(request.paymentCompletedAt)} /></td>
                              <td className="px-4 py-4"><RequestStatusBadge status={request.status} showIcon={false} /></td>
                              <td className="px-4 py-4">
                                <Button variant="ghost" size="sm" iconName="Eye" onClick={() => setSelectedInstanceId(task.id)}>
                                  Review
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-4 lg:hidden">
                  {visibleTasks.map((task) => {
                    const request = task.bookingRequest || {};
                    return (
                      <article key={task.id} className="rounded-xl border border-border bg-card p-4 font-caption">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h2 className="font-caption font-semibold text-foreground text-base mb-1">{request.clientName}</h2>
                            <p className="truncate text-sm text-muted-foreground">{getItemSummary(request)}</p>
                            <p className="mt-1 font-mono text-[11px] text-primary">{request.referenceCode}</p>
                          </div>
                          <RequestStatusBadge status={request.status} showIcon={false} />
                        </div>

                        <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-3">
                          <p className="text-xs text-muted-foreground">Current stage</p>
                          <p className="mt-1 text-sm text-foreground font-medium">{task.currentStage?.name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{task.workflowName}</p>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground">Total</p>
                            <p className="mt-1 text-foreground font-semibold">{formatRequestAmount(request.totalAmount)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Payment</p>
                            <div className="mt-1"><PaymentBadge paid={Boolean(request.paymentCompletedAt)} /></div>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          fullWidth
                          className="mt-4"
                          iconName="Eye"
                          onClick={() => setSelectedInstanceId(task.id)}
                        >
                          Review Task
                        </Button>
                      </article>
                    );
                  })}
                </div>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalCount={filteredTasks.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                  isLoading={isLoading}
                />
              </>
            )}
          </div>
        </main>
      </div>

      <WorkflowTaskDetailsModal
        instanceId={selectedInstanceId}
        isOpen={Boolean(selectedInstanceId)}
        onClose={() => setSelectedInstanceId(null)}
        onTasksChanged={handleTasksChanged}
      />
    </div>
  );
};

export default MyTasks;
